import { Injectable, isDevMode } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, finalize, map } from 'rxjs';
import { BaseHttpService } from '@app/core/http';
import { JwtService } from '@app/core/auth';
import { LoaderService } from '@app/services/loader.service';
import { Site } from '@app/models/site.model';
import { environment } from '../../../../environments/environment';

const STORAGE_KEY = 'gestion_produits_selectedSite';

@Injectable({ providedIn: 'root' })
export class SiteService extends BaseHttpService {
  private sitesSubject = new BehaviorSubject<Site[]>([]);
  public sites$ = this.sitesSubject.asObservable();

  private selectedSiteSubject = new BehaviorSubject<Site | null>(null);
  public selectedSite$ = this.selectedSiteSubject.asObservable();

  private sitesCache: Site[] | null = null;
  private sitesLoading = false;

  private readonly baseApiSpeedPresta = environment.baseApiSpeedpresta;

  constructor(
    http: HttpClient,
    private loaderService: LoaderService,
    jwtService: JwtService
  ) {
    super(jwtService, http);
  }

  private getOrgId(): string | undefined {
    return this.getUserByToken()?.organization?.toUpperCase();
  }

  loadSites(forceRefresh = false): void {
    if (!forceRefresh && this.sitesCache) {
      this.sitesSubject.next(this.sitesCache);
      return;
    }
    if (this.sitesLoading) return;

    const orgId = this.getOrgId();
    if (!orgId) return;

    this.sitesLoading = true;
    this.loaderService.startLoading();

    const body = {
      status: 'active',
      enterpriseId: orgId,
      options: { 'options.appId': 'SITES' },
      excluded: {}
    };

    this.retrieveAllFromKey<any>(
      `${this.baseApiSpeedPresta}${orgId}/${environment.platformSiteUrl}`,
      body
    ).pipe(
      map((sites: any[]) => (sites || []).map(site => {
        const options = site?.options || {};
        return {
          identifier: options.identifier || site?._id?.$oid || site?._id || '',
          label: options.label || '',
          address: options.adresse || '',
          description: options.description || '',
        } as Site;
      })),
      finalize(() => {
        this.loaderService.stopLoading();
        this.sitesLoading = false;
      })
    ).subscribe({
      next: (sites) => {
        this.sitesCache = sites;
        this.sitesSubject.next(sites);
      },
      error: (error) => {
        if (isDevMode()) console.error('[SiteService] Error loading sites:', error);
      }
    });
  }

  selectSite(site: Site): void {
    this.selectedSiteSubject.next(site);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(site));
    } catch (error) {
      if (isDevMode()) console.warn('[SiteService] localStorage write failed:', error);
    }
  }

  clearSelectedSite(): void {
    this.selectedSiteSubject.next(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  hydrateFromStorage(availableSites: Site[]): void {
    let stored: Site | null = null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) stored = JSON.parse(raw) as Site;
    } catch (error) {
      if (isDevMode()) console.warn('[SiteService] localStorage read failed:', error);
      stored = null;
    }

    if (!stored) return;

    const match = availableSites.find(s => s.identifier === stored!.identifier);
    if (match) {
      this.selectedSiteSubject.next(match);
    } else {
      this.clearSelectedSite();
    }
  }

  getCurrentSelectedSite(): Site | null {
    return this.selectedSiteSubject.value;
  }

  hasSiteSelected(): boolean {
    return this.selectedSiteSubject.value !== null;
  }

  getCurrentSites(): Site[] {
    return this.sitesSubject.value;
  }

  clearCache(): void {
    this.sitesCache = null;
    this.sitesLoading = false;
  }
}
